import React, { useEffect, useMemo, useState } from 'react';
import {
  AdminResearchChartsGetStateResponse,
  AdminResearchChartsStateDto,
  AdminUserDto,
  adminApi,
  isUnauthorized,
} from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

interface AdminResearchChartsScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized: () => void;
}

type SortKey = 'updatedAtUtc' | 'userId' | 'username' | 'stockSymbol' | 'id';

type PreviewState = {
  row: AdminResearchChartsStateDto;
  response: AdminResearchChartsGetStateResponse;
  prettyJson: string;
};

type PreviewSummaryRow = { label: string; value: string };

const AdminResearchChartsScreen: React.FC<AdminResearchChartsScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);

  const colors = useMemo(
    () => ({
      card: theme.cardBg,
      border: theme.border,
      text: theme.text,
      subText: theme.subText,
      hover: theme.hoverBg,
      inputBg: theme.inputBg,
      primary: theme.primary,
      danger: theme.danger,
      radius: theme.radius,
    }),
    [theme]
  );

  const [items, setItems] = useState<AdminResearchChartsStateDto[] | null>(null);
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  const [gridSearch, setGridSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'updatedAtUtc', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [criteriaDraft, setCriteriaDraft] = useState({ userIds: [] as number[], stockSymbol: '' });
  const [criteriaApplied, setCriteriaApplied] = useState(criteriaDraft);

  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [userPickerSearch, setUserPickerSearch] = useState('');

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 34,
    borderRadius: colors.radius,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.inputBg,
    padding: '0 10px',
    color: colors.text,
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 700,
  };

  const buttonStyle = (variant: 'primary' | 'neutral' | 'danger', disabled?: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: 34,
      padding: '0 12px',
      borderRadius: colors.radius,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.card,
      color: colors.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 13,
      fontWeight: 800,
      opacity: disabled ? 0.6 : 1,
      whiteSpace: 'nowrap',
    };

    if (variant === 'primary') return { ...base, border: 'none', backgroundColor: colors.primary, color: '#fff' };
    if (variant === 'danger') return { ...base, border: 'none', backgroundColor: colors.danger, color: '#fff' };
    return base;
  };

  const normalize = (v: unknown): string => (v ?? '').toString().trim().toLowerCase();

  const userLookup = useMemo(() => {
    const map = new Map<number, string>();
    (users ?? []).forEach((u) => map.set(u.id, u.username));
    return map;
  }, [users]);

  const selectedUsernames = useMemo(() => {
    const selected = new Set(criteriaDraft.userIds);
    return (users ?? [])
      .filter((u) => selected.has(u.id))
      .map((u) => u.username)
      .filter(Boolean);
  }, [criteriaDraft.userIds, users]);

  const filteredUsersForPicker = useMemo(() => {
    const q = (userPickerSearch ?? '').toString().trim().toLowerCase();
    const base = users ?? [];
    if (!q) return base;
    return base.filter((u) => {
      const uname = (u.username ?? '').toString().trim().toLowerCase();
      return uname.includes(q) || String(u.id).includes(q);
    });
  }, [userPickerSearch, users]);

  const refresh = async (opts?: { clearSelection?: boolean; applied?: typeof criteriaApplied }) => {
    setLoading(true);
    setError(null);
    try {
      const applied = opts?.applied ?? criteriaApplied;
      const stockSymbol = applied.stockSymbol?.trim() ? applied.stockSymbol.trim() : undefined;
      const userIds = (applied.userIds ?? []).filter((id) => Number.isFinite(id) && id > 0);

      const [states, usersList] = await Promise.all([
        adminApi.listResearchChartsStates(token, { userIds, stockSymbol }),
        adminApi.listUsers(token),
      ]);
      setItems(states);
      setUsers(usersList);
      if (opts?.clearSelection) setSelectedIds(new Set());
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed loading research charts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh({ clearSelection: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const visibleItems = useMemo(() => {
    const base = items ?? [];
    const q = normalize(gridSearch);
    const filtered = q
      ? base.filter((row) => {
          const username = (row.username ?? userLookup.get(row.userId) ?? '').toString();
          return (
            normalize(row.stockSymbol).includes(q) ||
            normalize(username).includes(q) ||
            normalize(row.userId).includes(q) ||
            normalize(row.id).includes(q)
          );
        })
      : base;

    const sorted = filtered.slice().sort((a, b) => {
      const dir = sort.dir === 'asc' ? 1 : -1;
      const getVal = (x: AdminResearchChartsStateDto): string | number => {
        switch (sort.key) {
          case 'updatedAtUtc':
            return x.updatedAtUtc ? new Date(x.updatedAtUtc).getTime() : 0;
          case 'userId':
            return x.userId;
          case 'username':
            return (x.username ?? userLookup.get(x.userId) ?? '').toString().toLowerCase();
          case 'stockSymbol':
            return (x.stockSymbol ?? '').toString().toLowerCase();
          case 'id':
            return x.id;
          default:
            return x.id;
        }
      };
      const av = getVal(a);
      const bv = getVal(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });

    return sorted;
  }, [gridSearch, items, sort.dir, sort.key, userLookup]);

  const pageCount = Math.max(1, Math.ceil(visibleItems.length / pageSize));
  const pageSafe = Math.min(pageCount, Math.max(1, page));
  const paged = visibleItems.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSafe]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const toggleAllOnPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const row of paged) {
        if (checked) next.add(row.id);
        else next.delete(row.id);
      }
      return next;
    });
  };

  const openPreview = async (row: AdminResearchChartsStateDto) => {
    setPreviewOpen(true);
    setPreviewBusy(true);
    setPreview(null);

    try {
      const res = await adminApi.getResearchChartsState(token, { userId: row.userId }, row.stockSymbol);
      const prettyJson = JSON.stringify((res as any)?.state ?? {}, null, 2);
      setPreview({ row, response: res, prettyJson });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setPreview({
        row,
        response: { userId: row.userId, stockSymbol: row.stockSymbol, state: {}, updatedAtUtc: null },
        prettyJson: `/* Failed loading state: ${e instanceof Error ? e.message : 'Unknown error'} */\n{}`,
      });
    } finally {
      setPreviewBusy(false);
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} Research Charts state(s)?`)) return;

    setLoading(true);
    setError(null);
    try {
      await adminApi.deleteResearchChartsStates(token, { ids });
      await refresh({ clearSelection: true });
      setPreviewOpen(false);
      setPreview(null);
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed deleting research charts states');
    } finally {
      setLoading(false);
    }
  };

  const getPreviewSummaryRows = (p: PreviewState | null): PreviewSummaryRow[] => {
    if (!p) return [];

    const stateObj: Record<string, unknown> | null =
      p.response?.state && typeof p.response.state === 'object' && !Array.isArray(p.response.state)
        ? (p.response.state as Record<string, unknown>)
        : null;

    const getStateString = (key: string): string => {
      if (!stateObj) return '—';
      const v = stateObj[key];
      if (v == null) return '—';
      if (typeof v === 'string') return v.trim() ? v : '—';
      if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '—';
      if (typeof v === 'boolean') return v ? 'true' : 'false';
      try {
        return JSON.stringify(v);
      } catch {
        return '—';
      }
    };

    const getStateStringFallback = (primaryKey: string, fallbackKey: string): string => {
      const primaryValue = getStateString(primaryKey);
      if (primaryValue !== '—') return primaryValue;
      return getStateString(fallbackKey);
    };

    const formatElementVisibility = (): string => {
      if (!stateObj) return '—';
      const ev = stateObj['elementVisibility'];
      if (!ev || typeof ev !== 'object' || Array.isArray(ev)) return '—';
      const entries = Object.entries(ev as Record<string, unknown>);
      const visible = entries.filter(([, v]) => v === true).map(([k]) => k);
      if (visible.length === 0) return 'none';
      const head = visible.slice(0, 12).join(', ');
      return visible.length > 12 ? `${head} (+${visible.length - 12})` : head;
    };

    const updated = p.row.updatedAtUtc
      ? new Date(p.row.updatedAtUtc).toISOString().slice(0, 19).replace('T', ' ')
      : p.response.updatedAtUtc
        ? new Date(p.response.updatedAtUtc).toISOString().slice(0, 19).replace('T', ' ')
        : '—';

    return [
      { label: 'User', value: `${p.row.userId}${p.row.username ? ` (${p.row.username})` : ''}` },
      { label: 'Stock', value: p.row.stockSymbol || '—' },
      { label: 'Updated', value: updated },
      { label: 'Period Type', value: getStateString('selectedPeriodType') },
      { label: 'Range', value: getStateString('selectedRange') },
      { label: 'Start Date', value: getStateString('startDate') },
      { label: 'End Date', value: getStateString('endDate') },
      { label: 'Analysis Date Mode (EPS)', value: getStateStringFallback('analysisDateModeEps', 'analysisDateMode') },
      { label: 'Analysis Date Mode (OCF)', value: getStateStringFallback('analysisDateModeOcf', 'analysisDateMode') },
      { label: 'Norm PE Input', value: getStateString('normalizedPEInput') },
      { label: 'Norm P/OCF Input', value: getStateString('normalizedPOCFInput') },
      { label: 'Entry Multiple (EPS)', value: getStateStringFallback('entryMultipleRatioEps', 'entryMultipleRatio') },
      { label: 'Exit Multiple (EPS)', value: getStateStringFallback('exitMultipleRatioEps', 'exitMultipleRatio') },
      { label: 'Entry Multiple (OCF)', value: getStateStringFallback('entryMultipleRatioOcf', 'entryMultipleRatio') },
      { label: 'Exit Multiple (OCF)', value: getStateStringFallback('exitMultipleRatioOcf', 'exitMultipleRatio') },
      { label: 'Visible Elements', value: formatElementVisibility() },
    ];
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: colors.radius,
          padding: 12,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr auto auto',
          gap: 10,
          alignItems: 'end',
        }}
      >
        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Users</div>
          <button
            type="button"
            style={{
              ...buttonStyle('neutral'),
              width: '100%',
              justifyContent: 'space-between',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
            onClick={() => setUserPickerOpen((v) => !v)}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {selectedUsernames.length === 0
                ? 'All users'
                : selectedUsernames.length === 1
                  ? selectedUsernames[0]
                  : `${selectedUsernames[0]}, +${selectedUsernames.length - 1}`}
            </span>
            <span style={{ color: colors.subText, fontWeight: 900 }}>{userPickerOpen ? '▲' : '▼'}</span>
          </button>

          {userPickerOpen ? (
            <div
              style={{
                position: 'absolute',
                zIndex: 10,
                top: 44,
                left: 0,
                right: 0,
                backgroundColor: colors.card,
                border: `1px solid ${colors.border}`,
                borderRadius: colors.radius,
                padding: 10,
                boxShadow: isDarkMode
                  ? '0 8px 24px rgba(0,0,0,0.45)'
                  : '0 8px 24px rgba(0,0,0,0.12)',
              }}
            >
              <input
                style={{ ...inputStyle, fontWeight: 700 }}
                placeholder="Search username…"
                value={userPickerSearch}
                onChange={(e) => setUserPickerSearch(e.target.value)}
              />

              <div style={{ marginTop: 10, maxHeight: 220, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(filteredUsersForPicker ?? []).map((u) => {
                  const checked = criteriaDraft.userIds.includes(u.id);
                  return (
                    <label
                      key={u.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '6px 8px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        backgroundColor: checked ? colors.hover : 'transparent',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setCriteriaDraft((prev) => {
                            const next = new Set(prev.userIds);
                            if (isChecked) next.add(u.id);
                            else next.delete(u.id);
                            return { ...prev, userIds: Array.from(next) };
                          });
                        }}
                      />
                      <span style={{ fontWeight: 900 }}>{u.username}</span>
                      <span style={{ color: colors.subText, fontWeight: 800 }}>#{u.id}</span>
                    </label>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button
                  type="button"
                  style={buttonStyle('neutral')}
                  onClick={() => setCriteriaDraft((p) => ({ ...p, userIds: [] }))}
                >
                  Clear
                </button>
                <button type="button" style={buttonStyle('neutral')} onClick={() => setUserPickerOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          ) : null}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Stock Symbol</div>
          <input
            value={criteriaDraft.stockSymbol}
            onChange={(e) => setCriteriaDraft((p) => ({ ...p, stockSymbol: e.target.value }))}
            placeholder="e.g. AAPL"
            style={inputStyle}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Grid Search</div>
          <input value={gridSearch} onChange={(e) => setGridSearch(e.target.value)} placeholder="Search" style={inputStyle} />
        </div>

        <button
          style={buttonStyle('primary', loading)}
          disabled={loading}
          onClick={() => {
            setCriteriaApplied(criteriaDraft);
            setPage(1);
            refresh({ clearSelection: true, applied: criteriaDraft });
          }}
        >
          Apply
        </button>
        <button style={buttonStyle('danger', loading || selectedIds.size === 0)} disabled={loading || selectedIds.size === 0} onClick={deleteSelected}>
          Delete Selected
        </button>
      </div>

      {error ? (
        <div
          style={{
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: colors.radius,
            padding: 12,
            color: colors.danger,
            fontWeight: 800,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : null}

      <div
        style={{
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: colors.radius,
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: colors.text }}>Research Charts States</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: colors.subText }}>Rows: {visibleItems.length}</div>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number.parseInt(e.target.value, 10) || 10);
                setPage(1);
              }}
              style={{ ...inputStyle, width: 90, height: 34, padding: '0 8px' }}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ borderTop: `1px solid ${colors.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: colors.inputBg, color: colors.subText, fontSize: 12 }}>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>
                  <input
                    type="checkbox"
                    checked={paged.length > 0 && paged.every((r) => selectedIds.has(r.id))}
                    onChange={(e) => toggleAllOnPage(e.target.checked)}
                  />
                </th>
                <th style={{ padding: '10px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('updatedAtUtc')}>
                  Updated
                </th>
                <th style={{ padding: '10px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('userId')}>
                  User ID
                </th>
                <th style={{ padding: '10px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('username')}>
                  Username
                </th>
                <th style={{ padding: '10px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('stockSymbol')}>
                  Stock
                </th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Start</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>End</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Norm PE</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Norm P/OCF</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Entry</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Exit</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('id')}>
                  ID
                </th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={13} style={{ padding: 12, color: colors.subText, fontWeight: 800 }}>
                    Loading…
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={13} style={{ padding: 12, color: colors.subText, fontWeight: 800 }}>
                    No rows
                  </td>
                </tr>
              ) : (
                paged.map((row) => {
                  const isSelected = selectedIds.has(row.id);
                  const username = (row.username ?? userLookup.get(row.userId) ?? '—').toString();
                  const updated = row.updatedAtUtc ? new Date(row.updatedAtUtc).toISOString().slice(0, 19).replace('T', ' ') : '—';
                  const start = (row.startDate ?? '—').toString();
                  const end = (row.endDate ?? '—').toString();
                  const normPe = (row.normalizedPE ?? '—').toString();
                  const normPocf = (row.normalizedPOCF ?? '—').toString();
                  const entry = (row.entryMultiple ?? '—').toString();
                  const exit = (row.exitMultiple ?? '—').toString();

                  return (
                    <tr
                      key={row.id}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.backgroundColor = colors.hover;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent';
                      }}
                      style={{ borderTop: `1px solid ${colors.border}`, color: colors.text, fontSize: 13 }}
                    >
                      <td style={{ padding: '10px 12px' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(row.id);
                              else next.delete(row.id);
                              return next;
                            });
                          }}
                        />
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: colors.subText, fontWeight: 800 }}>{updated}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontWeight: 900 }}>{row.userId}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{username}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontWeight: 900 }}>{row.stockSymbol}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: colors.subText, fontWeight: 800 }}>{start}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: colors.subText, fontWeight: 800 }}>{end}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 900 }}>{normPe}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 900 }}>{normPocf}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 900 }}>{entry}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 900 }}>{exit}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', textAlign: 'right', color: colors.subText, fontWeight: 800 }}>{row.id}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button style={buttonStyle('neutral')} onClick={() => openPreview(row)}>
                          Preview
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            padding: 12,
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText }}>
            Page {pageSafe} / {pageCount}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={buttonStyle('neutral', pageSafe <= 1)} disabled={pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Prev
            </button>
            <button
              style={buttonStyle('neutral', pageSafe >= pageCount)}
              disabled={pageSafe >= pageCount}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {previewOpen ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 18,
            zIndex: 1000,
          }}
          onMouseDown={() => {
            setPreviewOpen(false);
            setPreview(null);
          }}
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              width: 'min(980px, 96vw)',
              maxHeight: '90vh',
              overflow: 'auto',
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: colors.radius,
              padding: 14,
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: colors.text }}>State Preview</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  style={buttonStyle('neutral', previewBusy || !preview)}
                  disabled={previewBusy || !preview}
                  onClick={() => {
                    if (!preview) return;
                    void navigator.clipboard.writeText(preview.prettyJson);
                  }}
                >
                  Copy JSON
                </button>
                <button
                  style={buttonStyle('neutral')}
                  onClick={() => {
                    setPreviewOpen(false);
                    setPreview(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            <div style={{ marginTop: 10, fontSize: 12, fontWeight: 900, color: colors.subText }}>
              {preview?.row ? `User ${preview.row.userId} • ${preview.row.stockSymbol}` : ''}
            </div>

            {!previewBusy && preview ? (
              <div
                style={{
                  marginTop: 10,
                  borderRadius: colors.radius,
                  border: `1px solid ${colors.border}`,
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '10px 12px', backgroundColor: colors.inputBg, color: colors.subText, fontSize: 12, fontWeight: 900 }}>
                  Summary
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {getPreviewSummaryRows(preview).map((r) => (
                      <tr key={r.label} style={{ borderTop: `1px solid ${colors.border}` }}>
                        <td style={{ padding: '8px 12px', width: 180, color: colors.subText, fontSize: 12, fontWeight: 900, whiteSpace: 'nowrap' }}>
                          {r.label}
                        </td>
                        <td style={{ padding: '8px 12px', color: colors.text, fontSize: 12, fontWeight: 800, wordBreak: 'break-word' }}>
                          {r.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <pre
              style={{
                marginTop: 10,
                padding: 12,
                borderRadius: colors.radius,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.inputBg,
                color: colors.text,
                fontSize: 12,
                lineHeight: 1.5,
                overflowX: 'auto',
                whiteSpace: 'pre',
              }}
            >
              {previewBusy ? 'Loading…' : preview?.prettyJson ?? '{}'}
            </pre>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminResearchChartsScreen;
